import * as RadioGroup from '@radix-ui/react-radio-group'
import { orderBy } from 'lodash'
import { useRouter } from 'next/router'
import React, { useMemo, useRef, useState } from 'react'
import { ROUTE } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import type SwiperInstance from 'swiper'
import { A11y, Navigation } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import { EXPERT_BOOK_CONSULTATION_ID } from '@public/ExpertDetail/utils'
import { Button } from 'components/Button'
import { type Card } from 'components/Card'
import { GET_NOTIFIED_STEP } from 'components/GetNotifiedModal'
import Icon from 'components/Icon'
import NoData from 'components/NoData'
import { SessionBookingCard } from 'components/SessionBookingCard'
import { H5 } from 'components/Typography'
import { useCurrency } from 'providers/currency'
import {
  GA4_ECOM_CUSTOM_EVENTS,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, SPACE } from 'Theme'
import {
  type CalculatedPrice,
  type PublicExpertDetailConsultationTabQuery,
} from 'types/graphql-generated'
import { getPriceByCurrencyIsoCodeFromCalculatedPrices } from 'utils/currency'
import { formatWithExpertUser } from 'utils/formatters'
import { silentPushUrl } from 'utils/pushUrl'
import { StyledCard, StyledSwiper, StyledArrowButton } from './components'

const SESSION_BOOKING_CARDS_GAP = 12

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  &:hover {
    cursor: pointer;
    background-color: ${COLOR_INTENT.GRAY_3};
    transition: background-color 0.25s ease-in-out;
  }

  &[data-state='checked'] {
    border: 1px solid ${COLOR_INTENT.SESSION_BOOKING_CARD.SELECTED_BORDER};
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`

interface SessionBookingCardListingProps
  extends React.ComponentProps<typeof Card> {
  data: PublicExpertDetailConsultationTabQuery['expertBySlug']
  setGetNotifiedStep: React.Dispatch<React.SetStateAction<string>>
  setIsGetNotifiedModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsBookingModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedSession: React.Dispatch<
    React.SetStateAction<
      PublicExpertDetailConsultationTabQuery['expertBySlug']['sessions'][number] & {
        displayPrice?: CalculatedPrice
      }
    >
  >
}

export const SessionBookingCardListing: React.FC<
  SessionBookingCardListingProps
> = ({
  data,
  setGetNotifiedStep,
  setIsGetNotifiedModalOpen,
  setIsBookingModalOpen,
  setSelectedSession,
  ...props
}) => {
  const router = useRouter()
  const swiper = useRef(null)
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)
  const [isBeginning, setIsBeginning] = useState(false)
  const [isEnd, setIsEnd] = useState(false)

  // Order so the available sessions are visible first
  const orderedSessions = orderBy(data.sessions, 'isAvailable', 'desc')

  const [selectedSessionId, setSelectedSessionId] = useState<number>(
    orderedSessions.length > 0 ? orderedSessions[0].id : null
  )
  const { isNoneSignedIn, isClientSignedIn, isExpertSignedIn, isPageLoading } =
    useUserAuth()
  const selectedCurrency = useCurrency()
  const { executeDataToDataLayer } = useGoogleAnalytics()

  const isUserSignedInOrGuest =
    isNoneSignedIn || isClientSignedIn || isExpertSignedIn
  const isAbleToBook = isUserSignedInOrGuest && !isPageLoading
  const areArrowButtonsVisible = !isBeginning || !isEnd

  const selectedSession = useMemo(
    () => data.sessions.find((session) => session.id === selectedSessionId),
    [data, selectedSessionId]
  )

  const displayPrice = useMemo(
    () =>
      selectedSession
        ? getPriceByCurrencyIsoCodeFromCalculatedPrices(
            selectedCurrency.currencyIsoCode,
            selectedSession.price.calculatedPrices
          )
        : null,
    [selectedCurrency, selectedSession]
  )

  const isSessionAvailable = (session) =>
    data.hasRequiredServicesActivated && session?.isAvailable

  const handleShowAvailability = (session) => {
    if (isAbleToBook && isSessionAvailable(session)) {
      setSelectedSession({
        ...session,
        displayPrice,
      })
      setIsBookingModalOpen(true)
      executeDataToDataLayer({
        event: GA4_ECOM_CUSTOM_EVENTS.VIEW_CALENDAR_CONSULTATIONS,
        overrideData: {
          session,
        },
        includeStoredData: false,
      })
    }
  }

  const handleRequestAvailability = (session) => {
    if (isAbleToBook && !isSessionAvailable(session)) {
      setSelectedSession(session)
      setIsGetNotifiedModalOpen(true)
    }
  }

  return (
    <StyledCard
      id={EXPERT_BOOK_CONSULTATION_ID}
      as="article"
      display="flex"
      flexDirection="column"
      paddingSize={SPACE.PX_24}
      {...props}
    >
      {data.sessions?.length > 0 ? (
        <>
          <header>
            <H5 as="h3" mb={SPACE.PX_16}>
              Choose a consultation
            </H5>
          </header>
          <main>
            <StyledArrowButton
              ref={prevButtonRef}
              mb={SPACE.PX_4}
              isVisible={areArrowButtonsVisible}
            >
              <Icon.ChevronUpSmall />
            </StyledArrowButton>
            <form>
              <RadioGroup.Root
                aria-label="select session"
                disabled={!isAbleToBook}
                onValueChange={(sessionId) => {
                  setSelectedSessionId(Number(sessionId))
                }}
                defaultValue={orderedSessions[0].id.toString()}
              >
                <StyledSwiper
                  ref={swiper}
                  modules={[Navigation, A11y]}
                  navigation={{
                    prevEl: prevButtonRef.current,
                    nextEl: nextButtonRef.current,
                  }}
                  slidesPerView="auto"
                  direction="vertical"
                  allowTouchMove={false}
                  spaceBetween={
                    data.sessions.length >= 2 ? SESSION_BOOKING_CARDS_GAP : 0
                  }
                  onSwiper={(swiperInstance: SwiperInstance) => {
                    setIsBeginning(swiperInstance.isBeginning)
                    setIsEnd(swiperInstance.isEnd)
                  }}
                  watchOverflow
                  onProgress={(swiperInstance: SwiperInstance) => {
                    // We need to use `onProgress` instead of `onSlideChange`,
                    // because the latter is not triggered when we slide to the last slide.
                    // The issue is casued by slidesPerView="auto"
                    // https://github.com/nolimits4web/swiper/issues/3108
                    setIsBeginning(swiperInstance.isBeginning)
                    setIsEnd(swiperInstance.isEnd)
                  }}
                  isEnd={isEnd}
                  isBeginning={isBeginning}
                >
                  {orderedSessions.map((session) => {
                    const {
                      id,
                      title,
                      description,
                      lengthInMinutes,
                      expertUser,
                      price,
                    } = session
                    const {
                      title: expertUserTitle,
                      firstName,
                      lastName,
                    } = expertUser
                    const sessionDisplayPrice =
                      getPriceByCurrencyIsoCodeFromCalculatedPrices(
                        selectedCurrency.currencyIsoCode,
                        price.calculatedPrices
                      )
                    const expertName = formatWithExpertUser({
                      title: expertUserTitle,
                      firstName,
                      lastName,
                      isWithPrefix: false,
                    })
                    return (
                      <SwiperSlide key={id}>
                        <StyledRadioGroupItem
                          value={id.toString()}
                          disabled={!isAbleToBook}
                          asChild
                        >
                          <SessionBookingCard
                            title={title}
                            description={description}
                            expertName={expertName}
                            duration={lengthInMinutes}
                            price={price}
                            displayPrice={sessionDisplayPrice}
                            onDoubleClick={() =>
                              isSessionAvailable(session)
                                ? handleShowAvailability(session)
                                : handleRequestAvailability(session)
                            }
                          />
                        </StyledRadioGroupItem>
                      </SwiperSlide>
                    )
                  })}
                </StyledSwiper>
              </RadioGroup.Root>
            </form>
            <StyledArrowButton
              ref={nextButtonRef}
              mt={SPACE.PX_4}
              isVisible={areArrowButtonsVisible}
            >
              <Icon.ChevronDownSmall />
            </StyledArrowButton>
            {isAbleToBook && (
              <>
                {isSessionAvailable(selectedSession) && (
                  <Button
                    data-test-id={
                      DATA_TEST_ID.EXPERT_DETAIL_SHOW_AVAILABILITY_BTN
                    }
                    mt={SPACE.PX_16}
                    onClick={() => handleShowAvailability(selectedSession)}
                    aria-label="show availability"
                  >
                    Show availability
                  </Button>
                )}
                {!isSessionAvailable(selectedSession) && (
                  <Button
                    data-test-id={
                      DATA_TEST_ID.EXPERT_DETAIL_REQUEST_AVAILABILITY_BTN
                    }
                    mt={SPACE.PX_16}
                    onClick={() => handleRequestAvailability(selectedSession)}
                    aria-label="request availability"
                  >
                    Request Availability
                  </Button>
                )}
              </>
            )}
          </main>
        </>
      ) : (
        <NoData
          icon={Icon.Communication}
          header={`${data.profileName} has no consultations currently available.`}
          content={`Subscribe and get notified via email once ${data.profileName} becomes available.`}
          button={() => (
            <Button
              data-test-id={
                DATA_TEST_ID.EXPERT_DETAIL_REQUEST_NEW_AVAILABILITY_BTN
              }
              mt={SPACE.PX_16}
              onClick={() => {
                setIsGetNotifiedModalOpen(true)
                setGetNotifiedStep(GET_NOTIFIED_STEP.SUBMIT)
                silentPushUrl({
                  router,
                  route: ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
                    slug: data.slug,
                  }),
                  params: {
                    isGetNotifiedModalOpen: true,
                  },
                })
              }}
              aria-label="request new availability"
            >
              Request new availability
            </Button>
          )}
        />
      )}
    </StyledCard>
  )
}
